import React from 'react';
import {
  Home,
  Business,
  LocalShipping,
  Article,
  People,
  BarChart,
  PointOfSale,
} from '@mui/icons-material';

/**
 * Navigation configuration for the application
 * Extracted from Sidebar.js to be reusable across components
 * 
 * Structure:
 * - id: Unique identifier for the navigation item
 * - title: Display name
 * - icon: Material-UI icon component
 * - route: Navigation path (optional for groups with sub-items)
 * - color: Accent color for visual styling
 * - roles: Array of roles that can access this item ('all' means everyone)
 * - subItems: Array of child navigation items (optional)
 */
const navigationConfig = [
  // {
  //   id: 'home',
  //   title: 'Home',
  //   icon: <Home />,
  //   route: '/Home',
  //   color: '#4CAF50',
  //   roles: ['all'],
  // },
  {
    id: 'master',
    title: 'Master',
    icon: <Business />,
    color: '#2196F3', // Blue
    roles: ['all'],
    subItems: [
      {
        id: 'company',
        title: 'Company',
        route: '/CompanyEdit',
        color: '#2196F3',
        roles: ['all'],
      },
      {
        id: 'customer',
        title: 'Customer',
        route: '/CustomerIndex',
        color: '#FF9800',
        roles: ['all'],
      },
      {
        id: 'supplier',
        title: 'Supplier',
        route: '/SupplierIndex',
        color: '#FF9800',
        roles: ['all'],
      },
      {
        id: 'category',
        title: 'Category',
        route: '/CategoryIndex',
        color: '#009688',
        roles: ['all'],
      },
      {
        id: 'item',
        title: 'Item',
        route: '/ItemIndex',
        color: '#9C27B0',
        roles: ['all'],
      },
      {
        id: 'bank',
        title: 'Bank',
        route: '/BankIndex',
        color: '#F44336',
        roles: ['all'],
      },
      {
        id: 'account-head',
        title: 'Account Head',
        route: '/IncomeExpenseHeadIndex',
        color: '#8BC34A',
        roles: ['all'],
      },

    ],
  },
  {
    id: 'transaction',
    title: 'Transaction',
    icon: <PointOfSale />,
    color: '#dca9e6ff', // Purple
    roles: ['all'],
    subItems: [
      {
        id: 'purchase',
        title: 'Purchase',
        route: '/PurchaseIndex',
        color: '#d390dfff',
        roles: ['all'],
      },
      {
        id: 'food-register',
        title: 'Food Register',
        route: '/DailyFoodRegister',
        color: '#FF6B6B',
        roles: ['all'],
      },
    ],
  },
  {
    id: 'invoice',
    title: 'Invoice',
    icon: <LocalShipping />,
    color: '#FF6F00', // Dark Orange
    roles: ['all'],
    subItems: [
      {
        id: 'invoice-index',
        title: 'Invoice List',
        route: '/InvoiceIndex',
        color: '#FF6F00',
        roles: ['all'],
      },
      {
        id: 'invoice-generate',
        title: 'Invoice Generate',
        route: '/InvoiceGenerate',
        color: '#FF8F00',
        roles: ['all'],
      },
    ],
  },
  // {
  //   id: 'consignment',
  //   title: 'Consignment',
  //   icon: <LocalShipping />,
  //   route: '/InvoiceIndex',
  //   color: '#FF6F00', 
  //   roles: ['all'],
  // },
  {
    id: 'accounts',
    title: 'Accounts',
    icon: <Article />,
    color: '#00BCD4', // Cyan
    roles: ['all'],
    subItems: [
      {
        id: 'payment-receipt',
        title: 'Payment & Receipt',
        route: '/PaymentAndReceiptIndex',
        color: '#00BCD4',
        roles: ['all'],
      },
      {
        id: 'income-expense',
        title: 'Income & Expense',
        route: '/IncomeExpenseIndex',
        color: '#00ACC1',
        roles: ['all'],
      },
    ],
  },
  {
    id: 'auth-zen',
    title: 'Auth Zen',
    icon: <People />,
    color: '#E91E63', // Pink
    roles: ['all'],
    subItems: [
      {
        id: 'users',
        title: 'Users',
        route: '/UserIndex',
        color: '#E91E63',
        roles: ['all'],
      },
      {
        id: 'roles',
        title: 'Roles',
        route: '/RoleIndex',
        color: '#E91E63',
        roles: ['all'],
      },
      {
        id: 'reset-password',
        title: 'Reset Password',
        route: '/PasswordChange',
        color: '#E91E63',
        roles: ['all'],
      },
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: <BarChart />,
    color: '#FF5722', // Deep Orange
    roles: ['all'],
    subItems: [
      {
        id: 'ledger',
        title: 'Ledger',
        route: '/Ledger',
        color: '#FF5722',
        roles: ['all'],
      },
      {
        id: 'outstanding',
        title: 'Outstanding',
        route: '/Outstanding',
        color: '#FF5722',
        roles: ['all'],
      },
      {
        id: 'expense-report',
        title: 'Expense Report',
        route: '/ExpenseReport',
        color: '#FF5722',
        roles: ['all'],
      },
      {
        id: 'consignment-report',
        title: 'Consignment',
        route: '/InvoiceReport',
        color: '#FF5722',
        roles: ['all'],
      },
      {
        id: 'detailed-invoice',
        title: 'Detailed Invoice',
        route: '/DetailedInvoice',
        color: '#FF5722',
        roles: ['all'],
      },
    ],
  },
];

/**
 * Filters navigation items based on user role
 * @param {Array} config - Navigation configuration array
 * @param {string} roleName - User's role name
 * @returns {Array} Filtered navigation items
 */
export const filterNavigationByRole = (config, roleName) => {
  return config.filter((item) => {
    // If roles include 'all', everyone can access
    if (item.roles.includes('all')) {
      return true;
    }
    // Check if user's role is in the allowed roles
    return item.roles.includes(roleName);
  }).map((item) => {
    // If item has sub-items, filter them as well
    if (item.subItems) {
      return {
        ...item,
        subItems: item.subItems.filter((subItem) => {
          if (subItem.roles.includes('all')) {
            return true;
          }
          return subItem.roles.includes(roleName);
        }),
      };
    }
    return item;
  });
};

export default navigationConfig;
